// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data: playing a private dataset (docs/AYNI_DATA_INTAKE_PLAN.md,
 * "Streaming and delivery → In the app"; the API's §11–12).
 *
 * The delivery Worker wants `Authorization: Bearer <token>` on every
 * request, a token the site gives for ten minutes for one dataset. This
 * module holds one session per open dataset: the token, the address the
 * Worker serves it under (`base`, e.g.
 * `https://data.pachamama-studios.stream/t/<tenant>/d/<dataset>/`), and a
 * timer that renews the token before it expires while the dataset is still
 * shown.
 *
 * **The header goes to that address only.** `authHeaderFor(url)` answers
 * it for a URL on the session's origin whose path starts with the
 * session's path, after the URL is normalised (so `../` cannot walk out),
 * and for nothing else: never another host, never the site, never the
 * public stream host. It is attached in three places: `authorisedFetch`
 * (release pointer, descriptor, `.dsa`, MPD preflight, thumbnail and
 * colorbar), and dash.js' request interceptor (`dashAuthInterceptor`).
 * No token is ever put in an address: pictures are fetched with the header
 * and shown from object URLs, revoked when the session closes.
 *
 * The dataset itself is registered for the loader by its id (`ds` + 24
 * hex digits) in `dataService`'s private list, never in the catalogue or
 * its index.
 */

import { datasetToken, getOwnDataset, type DataToken } from './ownDataApi'
import { DATASET_ID } from './ownData'
import { parseErrorBudget, type ErrorBudget } from '../types/error-budget'

/** Renew at three quarters of the token's life, and never later than this before it ends. */
export const RENEW_BEFORE_MS = 90_000
/** After a failed renewal, try again this soon. */
export const RENEW_RETRY_MS = 15_000

interface Session {
  id: string
  origin: string
  /** The session's path prefix, ending in `/`. */
  path: string
  token: string
  issuedAt: number
  expiresAt: number
  timer: ReturnType<typeof setTimeout> | null
  objectUrls: string[]
}

const sessions = new Map<string, Session>()

/** Whether an id names an own dataset (rather than a catalogue row). */
export const isOwnDatasetId = (id: string): boolean => DATASET_ID.test(id)

/**
 * When to renew a token issued at `issuedAt` that ends at `expiresAt`:
 * at three quarters of its life, but at least `RENEW_BEFORE_MS` before it
 * ends, and not sooner than a second from now.
 */
export function renewalDelay(issuedAt: number, expiresAt: number, now: number): number {
  const life = Math.max(0, expiresAt - issuedAt)
  const at = Math.min(issuedAt + life * 0.75, expiresAt - RENEW_BEFORE_MS)
  return Math.max(1000, at - now)
}

/** The parsed scope a token may be sent to, or null for an address that is not one. */
export function tokenScope(base: string): { origin: string; path: string } | null {
  try {
    const u = new URL(base)
    if (u.protocol !== 'https:' && !(u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1'))) return null
    if (u.username || u.password || u.search || u.hash) return null
    const path = u.pathname.endsWith('/') ? u.pathname : `${u.pathname}/`
    // At least `/t/<tenant>/d/<dataset>/`: never a whole host.
    if (!/^\/t\/[^/]+\/d\/[^/]+\/$/.test(path)) return null
    return { origin: u.origin, path }
  } catch {
    return null
  }
}

/** Whether `url` falls inside a scope (after normalising it). */
export function inScope(url: string, scope: { origin: string; path: string }, baseHref?: string): boolean {
  let u: URL
  try {
    u = new URL(url, baseHref ?? (typeof location !== 'undefined' ? location.href : undefined))
  } catch {
    return false
  }
  if (u.username || u.password) return false
  return u.origin === scope.origin && u.pathname.startsWith(scope.path)
}

/** `Bearer <token>` for a URL inside an open session's scope, else null. */
export function authHeaderFor(url: string): string | null {
  for (const s of sessions.values()) {
    if (inScope(url, s)) return `Bearer ${s.token}`
  }
  return null
}

function withAuth(init: RequestInit | undefined, header: string): RequestInit {
  const headers = new Headers(init?.headers)
  headers.set('Authorization', header)
  // No cookie to the Worker: the token is the whole credential.
  return { ...init, headers, credentials: 'omit' }
}

/** `fetch`, adding the token for an open session's own address and nothing else. */
export const authorisedFetch: typeof fetch = (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
  const header = authHeaderFor(url)
  if (!header) return fetch(input, init)
  if (input instanceof Request) return fetch(new Request(input, withAuth(init, header)))
  return fetch(input, withAuth(init, header))
}

/** dash.js' request interceptor: the same rule, for the MPD and every segment. */
export const dashAuthInterceptor = <R extends { url: string; headers?: Record<string, string>; credentials?: RequestCredentials }>(request: R): Promise<R> => {
  const header = authHeaderFor(request.url)
  if (header) {
    request.headers = { ...(request.headers ?? {}), Authorization: header }
    request.credentials = 'omit'
  }
  return Promise.resolve(request)
}

/** Whether a dataset has a live session (a token that has not expired). */
export function hasSession(id: string, now = Date.now()): boolean {
  const s = sessions.get(id)
  return !!s && s.expiresAt > now
}

/** The token's end, for the tests and the UI. */
export function sessionExpiresAt(id: string): number | null {
  return sessions.get(id)?.expiresAt ?? null
}

/**
 * The meeting this browser is in, if any (decision O6, behind the site's
 * DATA_MEETING_SHARES): its room code goes with every token request, so a
 * presenter's private dataset shown to the meeting opens for everyone in
 * it, signed in or not, until the meeting ends or the presenter stops.
 * Cleared when the browser leaves the meeting; the next renewal then fails
 * and the session closes.
 */
let meetingRoom: string | null = null
/** The presenter's signed seat token, when this browser presents the meeting (it proves the seat to the site). */
let presenterToken: string | null = null

export function setMeetingRoom(code: string | null, presenter: string | null = null): void {
  meetingRoom = code
  presenterToken = code ? presenter : null
}

export function currentMeetingRoom(): string | null {
  return meetingRoom
}

/** The room and the presenter's seat token while this browser presents a meeting; else null. */
export function presentingMeeting(): { room: string; st: string } | null {
  return meetingRoom && presenterToken ? { room: meetingRoom, st: presenterToken } : null
}

/** Asks whether a dataset is still shown; a session whose dataset is not is closed at its next renewal. */
let stillShown: (id: string) => boolean = () => true

export function setPrivateDatasetInUse(test: (id: string) => boolean): void {
  stillShown = test
}

function schedule(s: Session, delay: number, fetchImpl?: typeof fetch): void {
  if (s.timer) clearTimeout(s.timer)
  s.timer = setTimeout(() => void renew(s.id, fetchImpl), delay)
}

async function renew(id: string, fetchImpl?: typeof fetch): Promise<void> {
  const s = sessions.get(id)
  if (!s) return
  if (!stillShown(id)) {
    closePrivateSession(id)
    return
  }
  const reply = await datasetToken(id, fetchImpl, meetingRoom)
  const now = Date.now()
  if (!sessions.has(id)) return
  if (!reply.ok) {
    // Not found or not ready any more (deleted, plan lapsed): stop. Anything else: try again soon, until it ends.
    if (reply.status === 404 || reply.status === 409 || reply.status === 401 || reply.status === 402 || reply.status === 403) {
      closePrivateSession(id)
      return
    }
    if (s.expiresAt > now) schedule(s, Math.min(RENEW_RETRY_MS, Math.max(1000, s.expiresAt - now)), fetchImpl)
    return
  }
  applyToken(s, reply.value, now)
  schedule(s, renewalDelay(now, s.expiresAt, now), fetchImpl)
}

function applyToken(s: Session, tok: DataToken, now: number): boolean {
  const scope = tokenScope(tok.base)
  if (!scope || scope.origin !== s.origin || scope.path !== s.path) return false
  s.token = tok.token
  s.issuedAt = now
  s.expiresAt = tok.expiresAt
  return true
}

export interface OpenedSession {
  id: string
  latest: string
  base: string
}

/**
 * Open (or keep) a session for an own dataset: a token, renewed while the
 * dataset is shown. Answers its addresses, or the route's error code.
 */
export async function openPrivateSession(id: string, fetchImpl?: typeof fetch): Promise<{ ok: true; value: OpenedSession } | { ok: false; error: string; status: number }> {
  if (!isOwnDatasetId(id)) return { ok: false, error: 'not_found', status: 404 }
  const reply = await datasetToken(id, fetchImpl, meetingRoom)
  if (!reply.ok) return { ok: false, error: reply.error, status: reply.status }
  const tok = reply.value
  const scope = tokenScope(tok.base)
  // The pointer must sit inside the scope the token is for.
  if (!scope || typeof tok.token !== 'string' || !inScope(tok.latest, scope)) return { ok: false, error: 'bad_answer', status: 502 }
  const now = Date.now()
  let s = sessions.get(id)
  if (s && (s.origin !== scope.origin || s.path !== scope.path)) {
    closePrivateSession(id)
    s = undefined
  }
  if (!s) {
    s = { id, origin: scope.origin, path: scope.path, token: tok.token, issuedAt: now, expiresAt: tok.expiresAt, timer: null, objectUrls: [] }
    sessions.set(id, s)
  } else {
    applyToken(s, tok, now)
  }
  schedule(s, renewalDelay(now, s.expiresAt, now), fetchImpl)
  return { ok: true, value: { id, latest: tok.latest, base: `${scope.origin}${scope.path}` } }
}

/** Fetch a picture of an open dataset with the token and show it from an object URL (revoked when the session closes). */
export async function privateObjectUrl(id: string, url: string): Promise<string | null> {
  const s = sessions.get(id)
  if (!s || !inScope(url, s)) return null
  try {
    const res = await authorisedFetch(url)
    if (!res.ok) return null
    const blob = await res.blob()
    if (!sessions.has(id)) return null
    const objectUrl = URL.createObjectURL(blob)
    s.objectUrls.push(objectUrl)
    return objectUrl
  } catch {
    return null
  }
}

/** Close a session: stop renewing, forget the token, revoke its pictures. */
export function closePrivateSession(id: string): void {
  const s = sessions.get(id)
  if (!s) return
  if (s.timer) clearTimeout(s.timer)
  for (const u of s.objectUrls) URL.revokeObjectURL(u)
  s.token = ''
  sessions.delete(id)
}

/** Close every session (signing out). */
export function closeAllPrivateSessions(): void {
  for (const id of [...sessions.keys()]) closePrivateSession(id)
}

/** A dataset's title from the site, for a link or reload that opens it by id. */
export async function ownDatasetTitle(id: string, fetchImpl?: typeof fetch): Promise<{ title: string; ready: boolean } | null> {
  const reply = await getOwnDataset(id, fetchImpl)
  if (!reply.ok) return null
  return { title: reply.value.dataset.title, ready: reply.value.dataset.state === 'ready' }
}

/** The budget the processor reported when each own dataset was reviewed, to compare with what was published. */
const reviewedBudgets = new Map<string, ErrorBudget>()

export function setReviewedBudget(id: string, raw: unknown): void {
  const b = parseErrorBudget(raw)
  if (b) reviewedBudgets.set(id, b)
  else reviewedBudgets.delete(id)
}

export function reviewedBudget(id: string): ErrorBudget | null {
  return reviewedBudgets.get(id) ?? null
}
